import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Inject,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { List } from 'immutable';
import { Observable, of } from 'rxjs';
import { isUndefined } from 'lodash';
import {
  IAccountDataResolveData,
  RESOLVE_KEY as accountDataResolveKey,
} from '../../../account-data/account-data.data';
import { BrowserManagerDataDispatcher } from '../../../browser-manager-data/browser-manager-data.dispatcher';
import { LocalStorageService } from '../../../browser-manager-data/local-storage.service';
import { ErrorDialogBuilder } from '../../../dialog/components/error-dialog/error-dialog.builder';
import {
  IFeaturesDataResolveData,
  RESOLVE_KEY as featuresDataResolveKey,
} from '../../../features-data/features-data.data';
import { FeaturesDataStore } from '../../../features-data/features-data.store';
import { BannersCanvases } from '../../../features-data/sub-models/banners-canvases.model';
import { Canvas } from '../../../features-data/sub-models/canvas.model';
import { PageCtas } from '../../../features-data/sub-models/page-ctas.model';
import { PurchaseInfo } from '../../../features-data/sub-models/purchase-info.model';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import { NavigationService } from '../../../navigation/navigation.service';
import { SettingsDataStore } from '../../../settings-data/settings-data.store';
import { Settings } from '../../../settings-data/settings.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { INavItem, MainBuilder } from './main.builder';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { I2gNgPerformanceService, MarkerNamesEnum } from 'i2g-ng-performance';

@Component({
  selector: 'app-main',
  templateUrl: 'main.component.html',
  styleUrls: ['main.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [MainBuilder, ErrorDialogBuilder],
})
export class MainComponent implements OnInit, AfterViewInit {
  @HostBinding('attr.class') componentCSSClass = 'app-page-wrapper';

  paywallCta: string;
  userEmail: string;
  isAccountMenuOpen: boolean;
  isPurchasable: boolean;
  invoice2goLogo: IIcon;
  trackingObject: ITrackingObject;
  presentedTrackingAction: ITrackingAction;
  companyName$: Observable<string>;
  navItemGroups$: Observable<List<List<INavItem>>>;
  canvases$: Observable<List<Canvas>>;
  showSideNavigation$: Observable<boolean>;
  hideSideNavigationIa$: Observable<boolean>;
  upgradeCta$: Observable<{ copy: string; action: ITrackingAction }>;
  isIaNavEnabled: boolean;

  constructor(
    private _route: ActivatedRoute,
    private _mainBuilder: MainBuilder,
    private _settingsDataStore: SettingsDataStore,
    private _featuresDataStore: FeaturesDataStore,
    private _navigationService: NavigationService,
    private _localStorageService: LocalStorageService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingDispatcher: TrackingDispatcher,
    private _browserManagerDataDispatcher: BrowserManagerDataDispatcher,
    private _i2gPerformanceService: I2gNgPerformanceService,
    @Inject(DOCUMENT) private _document: Document
  ) {}

  ngOnInit() {
    const routeData = this._route.snapshot.data;
    const featuresData: IFeaturesDataResolveData =
      routeData[featuresDataResolveKey];
    const accountData: IAccountDataResolveData =
      routeData[accountDataResolveKey];
    const toggles: Toggles = featuresData.toggles;
    const purchaseInfo: PurchaseInfo = featuresData.purchaseInfo;
    const bannersCanvases: BannersCanvases = featuresData.bannersCanvases;
    const trackingScreenName = TrackingScreenNamesEnum.navigation;

    this.isIaNavEnabled = toggles.isAllAccess(Toggles.KEYS.IA_NAV_EXPERIMENT);

    this.userEmail = accountData.user.email;
    this.isPurchasable = purchaseInfo.isPurchasable;
    this.paywallCta = purchaseInfo.paywallCta;
    this.invoice2goLogo = {
      name: 'invoice2go-logo',
      size: IconSizesEnum.custom,
    };
    this.trackingObject = this._trackingObjectBuilder.buildNavigation();
    this.presentedTrackingAction = this._trackingActionBuilder.buildPresented(
      trackingScreenName
    );

    this.upgradeCta$ = this._featuresDataStore.getCurrentPlan().pipe(
      map((plan) => {
        if (plan.isTrialWindow()) {
          return {
            copy: 'ACTION.SUBSCRIBE',
            action: this._trackingActionBuilder.buildButtonTapped(
              'subscribe',
              trackingScreenName
            ),
          };
        } else if (plan.isRenewalWindow()) {
          return {
            copy: 'ACTION.RENEW',
            action: this._trackingActionBuilder.buildButtonTapped(
              'renew',
              trackingScreenName
            ),
          };
        }
        return {
          copy: 'ACTION.UPGRADE',
          action: this._trackingActionBuilder.buildButtonTapped(
            'upgrade',
            trackingScreenName
          ),
        };
      }),
      shareReplay(1)
    );

    this.showSideNavigation$ = this._navigationService.navigationEnds$.pipe(
      switchMap(
        () =>
          (this._route.firstChild && this._route.firstChild.data) ||
          of({ showSideNavigation: true })
      ),
      map((data) =>
        isUndefined(data.showSideNavigation) ? true : data.showSideNavigation
      )
    );

    this.hideSideNavigationIa$ = this._navigationService.navigationEnds$.pipe(
      switchMap(
        () =>
          (this._route.firstChild && this._route.firstChild.data) ||
          of({ showSideNavigation: true })
      ),
      map((data) => data.hideSideNavigationIa)
    );

    this.canvases$ = this._featuresDataStore.canvases$;

    this.companyName$ = this._settingsDataStore.settings$.pipe(
      filter((x) => !!x),
      map((settings: Settings) => settings.companyInfo.name)
    );

    const defaultNavItemGroups = this._mainBuilder.buildNavItemGroups(toggles);

    this.navItemGroups$ = of(defaultNavItemGroups);

    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this.presentedTrackingAction
    );

    if (environment.features.featureFramework) {
      const pageCtas: PageCtas = bannersCanvases.pageCtas;
      const launch = pageCtas.launch;

      if (launch) {
        const appLaunchCta = launch.destination;
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        const localStorageKey = `PAGE_CTAS_${PageCtas.KEYS.LAUNCH}_CURRENT_VIEWS`;
        const currentViews: number =
          parseInt(this._localStorageService.getItem(localStorageKey), 10) || 0;
        const maxViews: number = launch.maxViews;
        const unlimitedViews: boolean = maxViews === -1;

        if (currentViews < maxViews || unlimitedViews) {
          setTimeout(() => {
            this._navigationService.goToDeepLink(appLaunchCta);

            this._browserManagerDataDispatcher.setLocalStorageItem(
              localStorageKey,
              `${unlimitedViews ? -1 : currentViews + 1}`
            );
          });
        }
      }
    }
  }

  ngAfterViewInit() {
    this._document.body.classList.add('loaded');

    this._i2gPerformanceService.mark(MarkerNamesEnum.time_to_interactive);
    const measurements = this._i2gPerformanceService.getLastMeasurements();

    this._trackingDispatcher.trackEvent(
      this._trackingObjectBuilder.buildPerformanceMetric(),
      this._trackingActionBuilder.buildAppLoaded(
        measurements[MarkerNamesEnum.time_to_first_paint],
        measurements[MarkerNamesEnum.time_to_app_load],
        measurements[MarkerNamesEnum.time_to_interactive]
      )
    );
  }

  onToggleMenu(isOpen: boolean) {
    this.isAccountMenuOpen = isOpen;
  }

  onUpgradeAction() {
    this._navigationService.goToDeepLink(this.paywallCta);
  }

  trackCanvasesByFn(index: number, canvas: Canvas): string {
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    return canvas.name + canvas.canvasConfig.hashCode();
  }
}
